import { useState, useEffect } from 'react';
import { Card, CardBody, Button, Spinner, Alert, Modal, ModalBody, Badge } from '@components/ui';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@components/ui';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon, XMarkIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useCustomers, useCreateCustomer, useUpdateCustomer, useDeleteCustomer } from '../hooks/useCustomers';
import { useQueryClient } from '@tanstack/react-query';
import { CustomerForm } from './CustomerForm';
import { CustomerImportModal } from './CustomerImportModal';
import type { Customer } from '../types/customer.types';
import { format } from 'date-fns';
import { useConsumption } from '@/features/subscriptions/hooks/useConsumption';
import Swal from 'sweetalert2';

const TAX_ID_TYPE_LABELS: Record<string, string> = {
  '01': 'Contribuyente',
  '02': 'Consumidor Final',
  '03': 'Gobierno',
  '04': 'Extranjero',
  'CONTRIBUYENTE': 'Contribuyente',
  'CONSUMIDOR_FINAL': 'Consumidor Final',
  'GOBIERNO': 'Gobierno',
  'EXTRANJERO': 'Extranjero',
};

export function CustomerListPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [_deletingCustomer, _setDeletingCustomer] = useState<Customer | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const queryClient = useQueryClient();

  // Debounce search input: wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only search if there are at least 3 characters or if it's empty (to show all)
      if (searchInput.length === 0 || searchInput.length >= 3) {
        setDebouncedSearch(searchInput);
        setPage(1); // Reset to first page when searching
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Detectar si es búsqueda por RUC (solo números y guiones) o por nombre
  const isRucSearch = /^[\d-]+$/.test(debouncedSearch);

  const { data, isLoading, error } = useCustomers({
    page,
    limit: 10,
    name: isRucSearch ? undefined : debouncedSearch,
    taxId: isRucSearch ? debouncedSearch : undefined,
  });
  const createMutation = useCreateCustomer();
  const updateMutation = useUpdateCustomer();
  const deleteMutation = useDeleteCustomer();
  const { validateCanAddCustomer } = useConsumption();

  // El backend ya pagina, usamos directamente los datos que devuelve
  const limit = data?.limit || 10;
  const totalPages = data?.totalPages || 1;
  const customers = data?.customers || [];

  const handleImportSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['customers'] });
    Swal.fire({
      icon: 'success',
      title: 'Importacion exitosa',
      text: 'Los clientes fueron importados correctamente',
      confirmButtonColor: '#3085d6',
    });
  };

  const handleOpenCreate = () => {
    setEditingCustomer(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCustomer(null);
  };

  const handleSubmit = async (data: Partial<Customer>) => {
    // Only validate when creating (not editing)
    if (!editingCustomer) {
      const validation = validateCanAddCustomer();

      if (!validation.allowed) {
        const result = await Swal.fire({
          title: 'Límite de clientes alcanzado',
          text: validation.reason,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Ver planes',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#3085d6',
        });

        if (result.isConfirmed) {
          // Navigate to plans page
          window.location.href = '/subscriptions/plans';
        }
        return;
      }
    }

    try {
      if (editingCustomer) {
        await updateMutation.mutateAsync({ id: editingCustomer.id, data });
        handleCloseModal();

        // Success message for update
        Swal.fire({
          title: '¡Actualizado!',
          text: 'El cliente ha sido actualizado exitosamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#3085d6',
        });
      } else {
        await createMutation.mutateAsync(data);
        handleCloseModal();

        // Reset to page 1 and clear search to see the new customer
        setPage(1);
        setSearchInput('');
        setDebouncedSearch('');

        // Success message for create
        Swal.fire({
          title: '¡Creado exitosamente!',
          text: 'El cliente ha sido creado exitosamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#3085d6',
        });
      }
    } catch (error) {
      console.error('❌ Error al guardar cliente:', error);

      // Error message
      Swal.fire({
        title: 'Error',
        text: 'Ocurrió un error al guardar el cliente. Por favor, intenta de nuevo.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#d33',
      });
    }
  };

  const handleDelete = async (customer: Customer) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: `Se eliminará al cliente "${customer.name}"`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      await deleteMutation.mutateAsync(customer.id);
      Swal.fire({
        title: 'Eliminado',
        text: 'El cliente ha sido eliminado exitosamente',
        icon: 'success',
        confirmButtonText: 'Aceptar',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">Clientes</h1>
          <p className="mt-2 text-secondary-600 dark:text-secondary-400">
            Gestiona tu cartera de clientes
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:gap-3 [&>*]:w-full sm:[&>*]:w-auto">
          <Button
            data-guide="Importar Clientes"
            variant="outline"
            leftIcon={<ArrowUpTrayIcon className="w-5 h-5" />}
            onClick={() => setIsImportModalOpen(true)}
          >
            Importar Excel
          </Button>
          <Button data-guide="Nuevo Cliente" leftIcon={<PlusIcon className="w-5 h-5" />} onClick={handleOpenCreate}>
            Nuevo Cliente
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardBody className="p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-secondary-400" />
              </div>
              <input
                type="text"
                placeholder="Buscar por nombre o RUC (mínimo 3 caracteres)..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-10 py-2 rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-secondary-400 hover:text-secondary-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
            {searchInput.length > 0 && searchInput.length < 3 && (
              <span className="text-sm text-secondary-500 dark:text-secondary-400">
                Escribe al menos 3 caracteres para buscar
              </span>
            )}
            {searchInput !== debouncedSearch && searchInput.length >= 3 && (
              <span className="text-sm text-secondary-500 dark:text-secondary-400 flex items-center gap-1">
                <Spinner size="sm" />
                Buscando...
              </span>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : error ? (
            <Alert variant="danger" title="Error al cargar clientes">
              Ocurrió un error al cargar los clientes. Por favor, intenta de nuevo.
            </Alert>
          ) : customers.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-white">
                No hay clientes
              </h3>
              <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                {debouncedSearch ? 'No se encontraron resultados para tu búsqueda' : 'Comienza creando tu primer cliente'}
              </p>
              {!debouncedSearch && (
                <div className="mt-6">
                  <Button onClick={handleOpenCreate}>
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Nuevo Cliente
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>RUC</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Fecha Creación</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell className="font-medium">
                      {customer.taxId}
                      {customer.taxIdSub && (
                        <Badge variant="default" size="sm" className="ml-2">
                          {customer.taxIdSub}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{customer.name}</TableCell>
                    <TableCell>
                      <Badge variant="info" size="sm">
                        {TAX_ID_TYPE_LABELS[customer.taxIdType] || customer.taxIdType}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-secondary-600 dark:text-secondary-400">
                      {customer.email || '-'}
                    </TableCell>
                    <TableCell className="text-secondary-600 dark:text-secondary-400">
                      {customer.phone || '-'}
                    </TableCell>
                    <TableCell className="text-secondary-600 dark:text-secondary-400">
                      {format(new Date(customer.createdAt), 'dd/MM/yyyy')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleOpenEdit(customer)}
                          className="text-primary-600 hover:text-primary-900 dark:hover:text-primary-400"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(customer)}
                          className="text-danger-600 hover:text-danger-900 dark:hover:text-danger-400"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Paginación */}
      {data && customers.length > 0 && (
        <Card>
          <CardBody className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <p className="text-sm text-secondary-700 dark:text-secondary-300">
                  Mostrando {((page - 1) * limit) + 1} a{' '}
                  {Math.min(page * limit, data.total)} de {data.total} clientes
                </p>
                <div className="text-xs text-secondary-500 dark:text-secondary-400">
                  Página {page} de {totalPages}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Modal Create/Edit */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingCustomer ? 'Editar Cliente' : 'Nuevo Cliente'}
        size="4xl"
      >
        <ModalBody>
          <CustomerForm
            key={editingCustomer?.id || 'new'}
            customer={editingCustomer || undefined}
            onSubmit={handleSubmit}
            onCancel={handleCloseModal}
            isLoading={createMutation.isPending || updateMutation.isPending}
          />
        </ModalBody>
      </Modal>

      {/* Modal Import Excel */}
      <CustomerImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={handleImportSuccess}
      />
    </div>
  );
}
